import { animate, AUTO_STYLE, state, style, transition, trigger } from '@angular/animations';
import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Customer, CustomerAccount } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';
import { ShepherdService } from 'angular-shepherd';
import debounce from 'lodash-es/debounce'; // for pixel tracking
import { PageScrollService } from 'ngx-page-scroll-core';

import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromAppRoot from '@app/app.reducers';
import * as fromStrategy from '@strategy/reducers';

import { MOBILE_VIEW_TRESHOLD } from '@app/app.constants';
import { CompanyListItem, Notification, SearchCompany } from '@shared/models/Strategy';
import { ThirdPartyServiceItem } from '@shared/models/ThirdPartService';
import { TrackingService } from '@shared/services/tracking.service';
import { environment } from 'src/environments/environment';

export enum PersonaliseTabs {
  PORTFOLIO = 'portfolio',
  RECOMMENDATIONS = 'inyovaStocks',
  SEARCH = 'search'
}

@Component({
  selector: 'app-strategy-personalise',
  templateUrl: './strategy-personalise.component.html',
  styleUrls: ['./strategy-personalise.component.scss'],
  animations: [
    trigger('collapse', [
      state('false', style({ height: AUTO_STYLE, visibility: AUTO_STYLE, overflow: 'visible' })),
      state('true', style({ height: '0', visibility: 'hidden', overflow: 'hidden' })),
      transition('false => true', animate('300ms cubic-bezier(0.25, 0.8, 0.25, 1)')),
      transition('true => false', animate('300ms 300ms cubic-bezier(0.25, 0.8, 0.25, 1)'))
    ])
  ],
  standalone: false
})
export class StrategyPersonaliseComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() is3aPillar: boolean;
  @Output() updateWishedCount = new EventEmitter<number>();

  activeGuidance = false;
  customer: Customer;
  currentAccount: CustomerAccount;
  showTab: string = PersonaliseTabs.PORTFOLIO;
  showCompanyDetail = false;
  showExcludedCompanies = false;
  portfolioStocks: CompanyListItem[];
  inyovaStocksCompanies: CompanyListItem[];
  wishlistCompanies: CompanyListItem[];
  blockedCompanies: CompanyListItem[];
  wishedCompaniesIDs = [];
  blockedCompaniesIDs = [];
  searchResultCompanies = [];
  searchResultCompaniesLoading = false;
  wishedCount = 0;
  leadActivityFired = false;
  isSearched = false;
  selectedCompanyID = null;
  loadingID = 0;
  suggestionSent = false;
  notification: Notification;
  notificationTimeout: number;
  showStabilityModal = false;
  personaliseTabs = PersonaliseTabs;
  showPortfolioWarningBox = false;
  showPortfolioUndoMessage = false;
  flipperBrokenPf$: Observable<ThirdPartyServiceItem>;
  isLoading = false;

  category: { key: string; image: string };
  suggestedCount = 5;
  companiesLoaded = false;

  protected readonly onDestroy$ = new Subject<void>();

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  @ViewChild('excludedDetail') excludedDetail: ElementRef<HTMLDivElement>;

  get showEmptyState(): boolean {
    return this.showTab === PersonaliseTabs.SEARCH && !this.searchResultCompaniesLoading && !this.searchResultCompanies.length && this.isSearched;
  }

  constructor(
    private store: Store,
    public router: Router,
    private scrollService: PageScrollService,
    private trackingService: TrackingService,
    private shepherdService: ShepherdService,
    private translateService: TranslateService,
    @Inject(DOCUMENT) private document: any
  ) {}

  ngOnInit(): void {
    this.store
      .select(fromAppRoot.selectCustomerAndCurrentInvestAccount)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(({ customer, currentAccountDetail }) => {
        this.customer = customer;
        this.currentAccount = currentAccountDetail;
      });

    this.store
      .select(fromStrategy.selectStrategyPersonaliseView)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(({ companyList, notification, searchCompaniesLoading, showPortfolioWarningBox, showPortfolioUndoMessage }) => {
        this.inyovaStocksCompanies = companyList.yova_stocks;
        this.handleNotification(notification);
        this.searchResultCompaniesLoading = searchCompaniesLoading;
        this.showPortfolioWarningBox = showPortfolioWarningBox;
        this.showPortfolioUndoMessage = showPortfolioUndoMessage;
      });

    this.store.pipe(select(fromStrategy.selectPortfolioCompanyList), takeUntil(this.onDestroy$)).subscribe((portfolioCompanyList) => {
      if (!portfolioCompanyList.yova_stocks.length && !portfolioCompanyList.wishlist.length) {
        return;
      }
      this.handlePortfolioCompanyList(portfolioCompanyList);
    });

    this.store.pipe(select(fromStrategy.selectSearchCompanies), takeUntil(this.onDestroy$)).subscribe((response) => {
      this.handleSearchCompaniesResult(response);
    });

    // TODO: Remove this after releasing broken portfolio dialog
    this.flipperBrokenPf$ = this.store.select(fromAppRoot.selectThirdPartyServices('broken_pf')) as Observable<ThirdPartyServiceItem>;

    // remove any selected company details
    this.store.dispatch(StrategyActions.emptyCompanyDetail());

    this.trackingService.trackView('/personalise');
    // Add 500ms debounce for better performance on search
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.searchCompany = debounce(this.searchCompany, 500);
  }

  ngAfterViewInit() {
    this.shepherdService.modal = true;
    this.shepherdService.defaultStepOptions = {
      scrollTo: false,
      cancelIcon: {
        enabled: false
      },
      arrow: false,
      highlightClass: 'highlight'
    };
    this.setupShepherdSteps();
    /* eslint-disable @typescript-eslint/no-explicit-any */
    /* eslint-disable @typescript-eslint/no-unsafe-member-access */
    (this.shepherdService.tourObject as any).options.exitOnEsc = false;
  }

  ngOnDestroy(): void {
    this.shepherdService.complete();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onShowExcludedCompanies() {
    if (this.showTab !== PersonaliseTabs.PORTFOLIO) {
      this.showTab = PersonaliseTabs.PORTFOLIO;
      this.store.dispatch(StrategyActions.emptyCompanyDetail());
      this.showCompanyDetail = false;
      this.clearSearch();
    }

    this.showExcludedCompanies = true;

    setTimeout(() => {
      this.scrollService.scroll({
        document: this.document,
        scrollTarget: this.excludedDetail.nativeElement
      });
    }, 300);
  }

  backToStrategy(): void {
    this.isLoading = true;
    this.trackingService.trackActivity('[Button]: Go to strategy');
    if (this.currentAccount.step_status === 1) {
      this.store.dispatch(StrategyActions.updateToAlmostProspect());
    } else {
      this.store.dispatch(StrategyActions.setTabIndex({ tabIndex: 5 }));
    }
  }

  searchCompany(): void {
    const searchedTerm = this.searchInput?.nativeElement?.value;
    this.isSearched = true;
    this.suggestionSent = false;

    if (searchedTerm === '') {
      this.clearSearch();
      return;
    }

    this.trackKeyword(searchedTerm);

    this.store.dispatch(StrategyActions.fetchSearchCompanies({ searchedTerm }));
  }

  trackKeyword(searchKeyword: string): void {
    this.trackingService.trackKeyword(searchKeyword).subscribe();
  }

  clearSearch(): void {
    this.isSearched = false;
    this.searchResultCompanies = [];
    this.searchResultCompaniesLoading = false;
    this.suggestionSent = false;
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
  }

  switchTab(tabName: string): void {
    if (this.showTab === tabName) {
      return;
    }

    this.store.dispatch(StrategyActions.emptyCompanyDetail());

    this.showTab = tabName;
    this.showCompanyDetail = false;
    this.showExcludedCompanies = false;
    this.clearSearch();

    if (this.activeGuidance) {
      return;
    }

    this.trackingService.trackActivity(`[Tab] ${tabName}`);
    if (tabName === this.personaliseTabs.RECOMMENDATIONS && this.portfolioStocks?.length > 0 && this.inyovaStocksCompanies?.length === 0) {
      this.trackingService.trackActivity('[View]: Recommendations: No suggestions seen');
    }
  }

  suggestCompany(companyName: string): void {
    // TODO: Lead activity page visit
    this.trackingService.trackActivity(`[Button] Customer suggested a company: ${companyName}`);
    this.store.dispatch(
      StrategyActions.sendCompanySuggestion({
        message: `${environment.name !== 'production' ? '[Staging]' : ''} ${companyName} has been suggested by the customer.`
      })
    );
    this.suggestionSent = true;
  }

  updateCompanyDetail(event: { companyID: number; updated: boolean }, detailsEl: HTMLElement): void {
    this.showCompanyDetail = event.updated;
    this.selectedCompanyID = event.companyID;

    if (!event.updated) {
      return;
    }

    setTimeout(() => {
      this.scrollService.scroll({
        document: this.document,
        scrollTarget: detailsEl,
        duration: 210,
        scrollOffset: window.innerWidth <= MOBILE_VIEW_TRESHOLD ? 120 : 80
      });
    }, 500);
  }

  handleNotification(notification: Notification) {
    this.notification = notification;
    if (notification.show) {
      return;
    }
    // clear timeout
    window.clearTimeout(this.notificationTimeout);
  }

  handlePortfolioCompanyList(portfolioCompanyList: {
    female_leadership: number;
    blacklist: CompanyListItem[];
    wishlist: CompanyListItem[];
    yova_stocks: CompanyListItem[];
  }) {
    this.blockedCompanies = this.sortList(portfolioCompanyList.blacklist);
    this.blockedCompaniesIDs = this.blockedCompanies.map((company) => company.id);

    this.portfolioStocks = this.sortByTopic([...portfolioCompanyList.yova_stocks]);

    let wishlistNodiscarded: CompanyListItem[] = [];
    let wishlistDiscarded: CompanyListItem[] = [];

    for (const wish of portfolioCompanyList.wishlist) {
      if (wish['is_included'] !== false) {
        wishlistNodiscarded = [...wishlistNodiscarded, wish];
      } else if (wish['is_included'] === false) {
        wishlistDiscarded = [...wishlistDiscarded, wish];
      }
    }

    if (wishlistNodiscarded.length) {
      this.sortByTopic(wishlistNodiscarded);
    }

    this.wishlistCompanies = [...wishlistDiscarded, ...wishlistNodiscarded];
    this.wishedCompaniesIDs = this.wishlistCompanies.map((company) => company.id);

    this.wishedCount = wishlistNodiscarded.length;

    // show notification when the strategy update and the notification has detail
    if (this.notification.company && this.notification.type) {
      this.store.dispatch(StrategyActions.setNotificationShowStatus({ show: true }));

      // use window.setTimeOut because it returns number
      this.notificationTimeout = window.setTimeout(() => {
        this.store.dispatch(StrategyActions.setNotification({ notification: { company: '', type: '', show: false } }));
      }, 3000);
    }

    this.updateWishedCount.emit(this.wishedCount);
    this.loadingID = 0;

    this.companiesLoaded = this.portfolioStocks?.length > 0 || this.wishlistCompanies?.length > 0;
  }

  hidePersonalisationGuidance() {
    this.shepherdService.complete();
    this.activeGuidance = false;
  }

  toggleExcludedCompanies() {
    this.showExcludedCompanies = !this.showExcludedCompanies;
  }

  handleSearchCompaniesResult(result: SearchCompany[]) {
    this.searchResultCompanies = result;

    if (!this.leadActivityFired && !result.length && this.showEmptyState) {
      this.trackingService.trackActivity(`[Input] Show empty companies serach results`);
      this.leadActivityFired = true;
    }
  }

  trackCTA(place: string): void {
    this.trackingService.trackActivity(place, true);
  }

  sortList(list: CompanyListItem[]): CompanyListItem[] {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return [...list].sort((a: CompanyListItem, b: CompanyListItem) => (a.name > b.name ? 1 : -1));
  }

  sortByTopic(list: CompanyListItem[]): CompanyListItem[] {
    if (list.length === 0) {
      return [];
    }

    return list.sort((a: CompanyListItem, b: CompanyListItem) => {
      if (a.criteria_dismatch?.length || b.criteria_dismatch?.length) {
        return b.criteria_dismatch.length - a.criteria_dismatch.length;
      } else {
        return b.topics.length - a.topics.length;
      }
    });
  }

  onOpenGuidance() {
    this.activeGuidance = true;
    if (this.showTab !== PersonaliseTabs.PORTFOLIO) {
      this.showTab = PersonaliseTabs.PORTFOLIO;
    }
    this.trackingService.trackActivity('[Button] Guidance: Show tutorial');
    setTimeout(() => {
      this.shepherdService.start();
    }, 250);
  }

  setupShepherdSteps() {
    this.shepherdService.addSteps([
      {
        attachTo: {
          element: '.personalise-guidance-target',
          on: 'top'
        },
        buttons: [
          {
            classes: 'ui-button secondary',
            text: `<span>${this.translateService.instant('walkthrough.personalisation.CTA.next') as string}</span>`,
            action: () => {
              this.trackingService.trackActivity('[Button] Guidance: NEXT view 1');
              this.switchTab(PersonaliseTabs.RECOMMENDATIONS);
              this.shepherdService.next();
            }
          },
          {
            classes: 'ui-button-text',
            text: `<span>${this.translateService.instant('walkthrough.personalisation.CTA.skip') as string}</span>`,
            action: () => {
              this.trackingService.trackActivity('[Button] Guidance: SKIP view 1');
              this.hidePersonalisationGuidance();
            }
          }
        ],
        title: this.translateService.instant('walkthrough.personalisation.1.title') as string,
        text: [
          `<p>${this.translateService.instant('walkthrough.personalisation.1.p1') as string}</p><p>${this.translateService.instant(
            'walkthrough.personalisation.1.p2'
          )}</p><p class="shepherd-icon-text"><span class="icon-i-heart"></span><span>${
            this.translateService.instant('new_strategy.matches_screen_b.favourite') as string
          }</span></p><p class="shepherd-icon-text"><span class="icon-i-blocked"></span><span>${
            this.translateService.instant('new_strategy.matches_screen_b.blocked') as string
          }</span></p><span class="shepherd-step">${this.translateService.instant('walkthrough.personalisation.steps.1')}</span>`
        ]
      },
      {
        attachTo: {
          element: '.personalise-guidance-target',
          on: 'top'
        },
        buttons: [
          {
            classes: 'ui-button secondary',
            text: `<span>${this.translateService.instant('walkthrough.personalisation.CTA.next') as string}</span>`,
            action: () => {
              this.trackingService.trackActivity('[Button] Guidance: NEXT view 2');
              this.switchTab(PersonaliseTabs.PORTFOLIO);
              this.shepherdService.next();
            }
          },
          {
            classes: 'ui-button-text',
            text: `<span>${this.translateService.instant('walkthrough.personalisation.CTA.skip') as string}</span>`,
            action: () => {
              this.trackingService.trackActivity('[Button] Guidance: SKIP view 2');
              this.hidePersonalisationGuidance();
            }
          }
        ],
        title: this.translateService.instant('walkthrough.personalisation.2.title') as string,
        text: [
          `<p>${this.translateService.instant('walkthrough.personalisation.2.p1') as string}</p><p>${
            this.translateService.instant('walkthrough.personalisation.2.p2') as string
          }</p><span class="shepherd-step">${this.translateService.instant('walkthrough.personalisation.steps.2') as string}</span>`
        ]
      },
      {
        attachTo: {
          element: '.personalise-action-target',
          on: 'top'
        },
        buttons: [
          {
            classes: 'ui-button secondary',
            text: `<span>${this.translateService.instant('walkthrough.personalisation.CTA.finish') as string}</span>`,
            action: () => {
              this.trackingService.trackActivity('[Button] Guidance: FINISH');
              this.hidePersonalisationGuidance();
            }
          }
        ],
        title: this.translateService.instant('walkthrough.personalisation.3.title') as string,
        text: [
          `<p>${this.translateService.instant('walkthrough.personalisation.3.p1') as string}</p><p>${this.translateService.instant(
            'walkthrough.personalisation.3.p2'
          )}</p><span class="shepherd-step">${this.translateService.instant('walkthrough.personalisation.steps.3') as string}</span>`
        ]
      }
    ]);
  }
}
